
/** Raw `attendance_status` values on a `description[]` student row. */
export const ATTENDANCE_STATUS = {
  PENDING: 0,
  ONGOING: 1,
  ATTENDED: 2,
  MISSED: 3,
  OFFLINE: 4,
  DEFER_REQUESTED: 5,
  CANCELLED: 6,
};

export const EDITABLE_ATTENDANCE_STATUSES = [
  ATTENDANCE_STATUS.PENDING,
  ATTENDANCE_STATUS.ONGOING,
  ATTENDANCE_STATUS.ATTENDED,
  ATTENDANCE_STATUS.MISSED,
];

/** Raw `cancel_status` values. `null`/absent means no defer workflow is open. */
export const CANCEL_STATUS = {
  REQUESTED: 0,
  APPROVED: 1,
  CANCELLED: 2,
  ONE_OFF_REQUESTED: 3,
};

/** Raw `cancel_defer_sources` values, which disambiguate `cancel_status`. */
export const DEFER_SOURCE = {
  DEFER: 1,
  ONE_OFF: 2,
};

/** Raw `fcount` / `wcount` markers, only meaningful alongside a cancelled attendance. */
export const SETTLEMENT_FLAG = {
  FORFEIT: 1,
  WRITEOFF: 2,
};

/** `sest_class` — which kind of session template produced this slot. */
export const SESSION_CLASS = {
  REGULAR: 1,
  ONE_OFF: 2,
  MAKE_UP: 3,
};

/** `fw_type` expected by `changeStudentEnrolmentForfeitWriteoff`. */
export const FORFEIT_TYPE = {
  FORFEIT: 1,
  WRITEOFF: 2,
};

/** `pause_apply_type` for `createStudentPause`. */
export const PAUSE_APPLY_TYPE = {
  ALL: 1,
  THIS_SESSION: 2,
};

/**
 * Delivery mode for a single student in a session. `enrol_category` is a string on
 * the wire ("In Centre" / "Online"); flexi is a separate boolean column.
 */
export const DELIVERY_MODE = {
  IN_CENTRE: 'inCentre',
  ONLINE: 'online',
  FLEXI: 'flexi',
};

/** Wire values accepted by `EditEnrolCategory`, mirroring `options_enrol_category`. */
export const ENROL_CATEGORY = {
  IN_CENTRE: 'In Centre',
  ONLINE: 'Online',
};

export const DELIVERY_MODE_META = {
  [DELIVERY_MODE.IN_CENTRE]: { label: 'In Centre', shortLabel: 'Centre' },
  [DELIVERY_MODE.ONLINE]: { label: 'Online', shortLabel: 'Online' },
  [DELIVERY_MODE.FLEXI]: { label: 'Flexi', shortLabel: 'Flexi' },
};

export const DISPLAY_STATUS = {
  PENDING: 'pending',
  ONGOING: 'ongoing',
  ATTENDED: 'attended',
  MISSED: 'missed',
  OFFLINE: 'offline',
  CANCELLED: 'cancelled',
  PAUSED: 'paused',
  FORFEIT: 'forfeit',
  WRITEOFF: 'writeoff',
  DEFER_REQUESTED: 'deferRequested',
  SLOT_ASSIGN: 'slotAssign',
  ONE_OFF_REQUESTED: 'oneOffRequested',
  /** Initial-diagnostics schedule statuses (isschedule === 1). */
  DIAGNOSTICS_CLOSED: 'diagnosticsClosed',
  DIAGNOSTICS_COMPLETED: 'diagnosticsCompleted',
  STUDENT_CONVERTED: 'studentConverted',
};

export const DISPLAY_STATUS_META = {
  [DISPLAY_STATUS.PENDING]: { label: 'Pending', tone: 'info', struckThrough: false },
  [DISPLAY_STATUS.ONGOING]: { label: 'Ongoing', tone: 'live', struckThrough: false },
  [DISPLAY_STATUS.ATTENDED]: { label: 'Attended', tone: 'success', struckThrough: false },
  [DISPLAY_STATUS.MISSED]: { label: 'Missed', tone: 'danger', struckThrough: false },
  [DISPLAY_STATUS.OFFLINE]: { label: 'Offline', tone: 'neutral', struckThrough: false },
  [DISPLAY_STATUS.CANCELLED]: { label: 'Cancelled', tone: 'neutral', struckThrough: true },
  [DISPLAY_STATUS.PAUSED]: { label: 'Paused', tone: 'danger', struckThrough: false },
  [DISPLAY_STATUS.FORFEIT]: { label: 'Forfeit', tone: 'danger', struckThrough: true },
  [DISPLAY_STATUS.WRITEOFF]: { label: 'Writeoff', tone: 'danger', struckThrough: true },
  [DISPLAY_STATUS.DEFER_REQUESTED]: { label: 'Defer Requested', tone: 'warning', struckThrough: false },
  [DISPLAY_STATUS.SLOT_ASSIGN]: { label: 'Slot Assign', tone: 'warning', struckThrough: false },
  [DISPLAY_STATUS.ONE_OFF_REQUESTED]: { label: 'One Off Requested', tone: 'warning', struckThrough: false },
  [DISPLAY_STATUS.DIAGNOSTICS_CLOSED]: { label: 'Closed', tone: 'danger', struckThrough: false },
  [DISPLAY_STATUS.DIAGNOSTICS_COMPLETED]: {
    label: 'Diagnostics Completed',
    tone: 'warning',
    struckThrough: false,
  },
  [DISPLAY_STATUS.STUDENT_CONVERTED]: {
    label: 'Student Converted',
    tone: 'warning',
    struckThrough: false,
  },
};

/** Synthetic tutor key so all assessment / diagnostics slots share one day-view lane. */
export const DIAGNOSTICS_LANE_KEY = '__diagnostics__';

/** Blue lane colour matching production `card_blue` diagnostics cards. */
export const DIAGNOSTICS_LANE_COLOR = {
  edge: '#5a9eb0',
  surface: '#dbeef4',
  ink: '#1c4a56',
};

/**
 * Extra chip keys that live in the Status filter but are not display statuses
 * (delivery-mode / marker filters).
 */
export const FILTER_CHIP = {
  FLEXI: 'flexi',
  IN_CENTRE: 'inCentre',
  INITIAL_DIAGNOSTICS: 'initialDiagnostics',
};

/**
 * Statuses offered as filter chips, in the order they appear in the filter panel.
 * Empty selection means "all". Multiselect is OR across chips; `FILTER_CHIP.FLEXI`
 * / `IN_CENTRE` match delivery mode; `INITIAL_DIAGNOSTICS` matches `isschedule` slots.
 */
export const FILTERABLE_STATUSES = [
  DISPLAY_STATUS.PENDING,
  DISPLAY_STATUS.ONGOING,
  DISPLAY_STATUS.ATTENDED,
  DISPLAY_STATUS.MISSED,
  FILTER_CHIP.IN_CENTRE,
  DISPLAY_STATUS.DEFER_REQUESTED,
  DISPLAY_STATUS.SLOT_ASSIGN,
  FILTER_CHIP.INITIAL_DIAGNOSTICS,
  DISPLAY_STATUS.ONE_OFF_REQUESTED,
  DISPLAY_STATUS.PAUSED,
  DISPLAY_STATUS.CANCELLED,
  DISPLAY_STATUS.FORFEIT,
  DISPLAY_STATUS.WRITEOFF,
  FILTER_CHIP.FLEXI
];

/** Labels for non-status chips in the Status filter section. */
export const FILTER_CHIP_META = {
  [FILTER_CHIP.FLEXI]: { label: 'Flexi' },
  [FILTER_CHIP.IN_CENTRE]: { label: 'In Centre' },
  [FILTER_CHIP.INITIAL_DIAGNOSTICS]: { label: 'Initial Diagnostics' },
};

/** Statuses that make a session "need attention" in the quick filters. */
export const NEEDS_ATTENTION_STATUSES = [
  DISPLAY_STATUS.MISSED,
  DISPLAY_STATUS.DEFER_REQUESTED,
  DISPLAY_STATUS.SLOT_ASSIGN,
  DISPLAY_STATUS.ONE_OFF_REQUESTED,
  DISPLAY_STATUS.PAUSED,
];

/** Which primary panel the content column is showing. */
export const CALENDAR_VIEW = {
  DAY: 'day',
  WEEK: 'week',
  DEFER_REQUESTS: 'deferRequests',
  DEFER_BIN: 'deferBin',
  ONE_OFF_REQUESTS: 'oneOffRequests',
  DISAPPROVED: 'disapproved',
};

/** Card density inside the day/week grids. */
export const DENSITY = {
  DETAILED: 'detailed',
  COMPACT: 'compact',
};

/**
 * Day-view layout mode.
 * - `TUTOR`: compressed per-tutor columns (current swimlane).
 * - `TIMELINE`: shared hour axis; sessions span start→end in each tutor row.
 */
export const DAY_LAYOUT = {
  TUTOR: 'tutor',
  TIMELINE: 'timeline',
};

/**
 * `checkOP` values understood by `getAllSessionFromTimeCal_defer`. "2" restricts the
 * grid to centre operating hours, "1" shows the full 24-hour day.
 */
export const HOURS_FILTER = {
  ALL_HOURS: '1',
  OPERATING_HOURS: '2',
};

/** Quick filters above the tutor list in the right-hand panel. */
export const QUICK_FILTER = {
  ALL: 'all',
  NEEDS_ATTENTION: 'needsAttention',
  ONLINE: 'online',
  IN_CENTRE: 'inCentre',
  FLEXI: 'flexi',
};

/**
 * Lane accent colours, assigned to tutors by index so each swimlane and its cards
 * share a hue. Chosen to stay legible against the `#f7f8fa` grid background.
 */
export const TUTOR_LANE_COLORS = [
  { edge: '#0e7c66', surface: '#eafaf5', ink: '#0b5d4d' },
  { edge: '#5b53c9', surface: '#f1f0fd', ink: '#413a9e' },
  { edge: '#c2650f', surface: '#fdf3e8', ink: '#8f4a0c' },
  { edge: '#1e6fbf', surface: '#ecf4fd', ink: '#164e88' },
  { edge: '#0f7490', surface: '#e9f6fa', ink: '#0b5567' },
  { edge: '#a83a5b', surface: '#fdeff3', ink: '#7c2a43' },
  { edge: '#4d7c0f', surface: '#f3f9e8', ink: '#3a5d0b' },
  { edge: '#8b5cf6', surface: '#f5f1fe', ink: '#5b3bb5' },
];

/** Avatar background colours for the student piles, cycled by index. */
export const STUDENT_AVATAR_COLORS = [
  '#0f7490',
  '#5b53c9',
  '#0e7c66',
  '#c2650f',
  '#a83a5b',
  '#4d7c0f',
  '#1e6fbf',
  '#7c3aed',
];

/** Fallback grid window when the centre has no operating hours configured. */
export const DEFAULT_HOUR_WINDOW = { startHour: 8, endHour: 21 };

/** How many student avatars a compact card shows before collapsing into "+N". */
export const COMPACT_AVATAR_LIMIT = 3;

/** How many student rows a week-view card listed before "+N more" (kept for compact only). */
export const WEEK_CARD_STUDENT_LIMIT = 3;

/** Standard autoClose used for every toast raised by this screen. */
export const TOAST_AUTO_CLOSE_MS = 700;

/** Minimum pixel width of one hour / empty column in the day swimlane. */
export const SWIMLANE_HOUR_MIN_WIDTH_PX = 150;

/** Minimum pixel width of a session card column (detailed content needs more room). */
export const SWIMLANE_SESSION_MIN_WIDTH_PX = 260;

/** Vertical space reserved for one overlapping card stack row in a tutor lane. */
export const SWIMLANE_STACK_ROW_HEIGHT_PX = 108;

/** Pixel height of one hour row in the week grid (session stretch uses this). */
export const WEEK_HOUR_HEIGHT_PX = 84;
